import { FieldTypes } from 'interfaces/shared.interface';

export enum ButtonVariants {
  PRIMARY = 'primary',
  OUTLINE = 'outline',
  PLAIN = 'plain',
  DESTRUCTIVE = 'destructive',
  DISABLED = 'disabled',
}

export enum ButtonSizeVariants {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export enum AvatarSizeVariants {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XL2 = 'XL2',
}

export enum BoxVariants {
  MAIN = 'main',
  TRANSPARENT = 'transparent',
  ROUNDED = 'rounded',
}

export const LANGUAGE_VARIANTS = {
  UA: 'uk-UA',
  UK: 'en-GB',
  USA: 'en',
};

export enum TagVariant {
  INFORMATIVE = 'informative',
  SUCCESS = 'success',
  WARNING = 'warning',
  CRITICAL = 'critical',
  NEUTRAL = 'neutral',
}

export const ButtonVariantStyles: { [key in ButtonVariants]: string } = {
  [ButtonVariants.PRIMARY]:
    'text-sm text-white-00 bg-primary-500 hover:bg-primary-600 active:bg-primary-700 rounded-md',
  [ButtonVariants.OUTLINE]:
    'text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 active:bg-neutral-100 dark:active:bg-neutral-900 border border-neutral-200 dark:border-neutral-200 rounded-md px-4',
  [ButtonVariants.PLAIN]:
    'text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 active:bg-neutral-100 dark:active:bg-neutral-900 rounded-md px-4',
  [ButtonVariants.DESTRUCTIVE]:
    'text-sm text-white-00 bg-red-500 hover:bg-red-600 active:bg-red-700 rounded-md py-2.5 px-4',
  [ButtonVariants.DISABLED]: 'text-sm text-neutral-400 bg-neutral-100 rounded-md cursor-not-allowed px-4',
};

export const ButtonSizeVariantsStyles: { [key in ButtonSizeVariants]: string } = {
  [ButtonSizeVariants.SM]: 'py-0.5 px-2 h-6',
  [ButtonSizeVariants.MD]: 'py-1.5 px-3 h-8',
  [ButtonSizeVariants.LG]: 'py-2.5 px-4 h-10',
};

export const AvatarSizeVariantsStyles: { [key in AvatarSizeVariants]: string } = {
  [AvatarSizeVariants.XS]: 'h-5 w-5',
  [AvatarSizeVariants.SM]: 'h-6 w-6',
  [AvatarSizeVariants.MD]: 'h-8 w-8',
  [AvatarSizeVariants.LG]: 'h-10 w-10',
  [AvatarSizeVariants.XL]: 'h-16 w-16',
  [AvatarSizeVariants.XL2]: 'h-[6.25rem] w-[6.25rem]',
};

export const AvatarFontSizeVariants: Record<AvatarSizeVariants, string> = {
  [AvatarSizeVariants.XS]: 'text-[8px]',
  [AvatarSizeVariants.SM]: 'text-[10px]',
  [AvatarSizeVariants.MD]: 'text-xs',
  [AvatarSizeVariants.LG]: 'text-sm',
  [AvatarSizeVariants.XL]: 'text-xl',
  [AvatarSizeVariants.XL2]: 'text-3xl',
};

export const BoxVariantsStyles: { [key in BoxVariants]: string } = {
  [BoxVariants.MAIN]: 'bg-white-00 dark:bg-black-900 border-1 border-neutral-200 dark:border-neutral-800',
  [BoxVariants.TRANSPARENT]: 'border border-neutral-200 dark:border-neutral-800 bg-white-00 dark:bg-black-900',
  [BoxVariants.ROUNDED]: 'bg-white-00 dark:bg-black-900 border-1 border-neutral-200 dark:border-neutral-800 rounded-md',
};

export const TagColorStyles: Record<
  TagVariant,
  {
    base: {
      containerStyles: string;
      labelStyles: string;
      checkboxStyles: string;
      closeIconStyles: string;
    };
    second: {
      containerStyles: string;
      labelStyles: string;
      checkboxStyles: string;
      closeIconStyles: string;
    };
  }
> = {
  [TagVariant.INFORMATIVE]: {
    base: {
      containerStyles: 'bg-blue-500',
      labelStyles: 'text-white',
      checkboxStyles: 'border-white',
      closeIconStyles: 'text-white',
    },
    second: {
      containerStyles: 'bg-blue-100',
      labelStyles: 'text-blue-600',
      checkboxStyles: 'accent-white border-blue-600 tag-second-variant-blue',
      closeIconStyles: 'text-blue-600',
    },
  },
  [TagVariant.SUCCESS]: {
    base: {
      containerStyles: 'bg-green-500',
      labelStyles: 'text-white',
      checkboxStyles: 'accent-white ',
      closeIconStyles: 'text-white',
    },
    second: {
      containerStyles: 'bg-green-100',
      labelStyles: 'text-green-600',
      checkboxStyles: 'accent-green-600 border-green-600 tag-second-variant-green',
      closeIconStyles: 'text-green-600',
    },
  },
  [TagVariant.WARNING]: {
    base: {
      containerStyles: 'bg-yellow-500',
      labelStyles: 'text-white',
      checkboxStyles: 'accent-white',
      closeIconStyles: 'text-white',
    },
    second: {
      containerStyles: 'bg-yellow-100',
      labelStyles: 'text-yellow-600',
      checkboxStyles: 'accent-yellow-600 border-yellow-600 tag-second-variant-yellow',
      closeIconStyles: 'text-yellow-600',
    },
  },
  [TagVariant.CRITICAL]: {
    base: {
      containerStyles: 'bg-red-500',
      labelStyles: 'text-white',
      checkboxStyles: 'accent-white',
      closeIconStyles: 'text-white',
    },
    second: {
      containerStyles: 'bg-red-100',
      labelStyles: 'text-red-600',
      checkboxStyles: 'accent-red-600 border-red-600 tag-second-variant-red',
      closeIconStyles: 'text-red-600',
    },
  },
  [TagVariant.NEUTRAL]: {
    base: {
      containerStyles: 'bg-neutral-500',
      labelStyles: 'text-white',
      checkboxStyles: 'accent-white',
      closeIconStyles: 'text-white',
    },
    second: {
      containerStyles: 'bg-neutral-100',
      labelStyles: 'text-neutral-600',
      checkboxStyles: 'accent-neutral-600 border-neutral-600 tag-second-variant-gray',
      closeIconStyles: 'text-neutral-600',
    },
  },
};

export enum LOCAL_STORAGE_KEYS {
  AUTH_TOKEN = 'auth_token',
}

export enum COOKIE_KEYS {
  AUTH_TOKEN = 'Abp.AuthToken',
  ENC_AUTH_TOKEN = 'enc_auth_token',
  LOCALIZATION_CULTURE_NAME = 'Abp.Localization.CultureName',
  STORYBOOK_AUTH_TOKEN = 'storybook_auth_token',
}

export enum BASE_FILTER_FIELDS {
  SEARCH_FIELD = 'filter',
}

export enum BASE_TEXT_AREA_FIELDS {
  TEXT_AREA_FIELD = 'textArea',
}

export enum BASE_PERMISSIONS_FROM_FIELD_NAME {
  PERMISSIONS = 'permissions',
}

export const FORM_INPUTS_WITHOUT_REFS = [FieldTypes.SELECT, FieldTypes.NUMBER, FieldTypes.DATE];

export const NO_DATA = '-';
